import { useRef, useState, useCallback } from "preact/hooks";
import type { JSX } from "preact";

import { getClerkAuthToken } from "../../auth/clerk";
import { checkReputationExists } from "../../lib/reputation-client";
import {
  computeSHA256,
  validateUploadFile,
  uploadSample,
} from "../../lib/upload-client";

type UploadState =
  | { kind: "idle" }
  | { kind: "invalid"; reason: string }
  | { kind: "has-file"; file: File; sha256: string }
  | { kind: "checking"; file: File; sha256: string }
  | { kind: "found"; file: File; sha256: string; verdict: string }
  | { kind: "ready"; file: File; sha256: string }
  | { kind: "uploading"; file: File; sha256: string }
  | { kind: "error"; message: string; file?: File; sha256?: string };

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Self-contained file upload zone with drop/browse, SHA-256 hashing,
 * reputation check, and upload flow.
 */
export function UploadZone() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<UploadState>({ kind: "idle" });
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFile = useCallback(async (file: File | null) => {
    if (!file) {
      setState({ kind: "idle" });
      return;
    }

    // Validate file
    const validation = validateUploadFile(file);
    if (!validation.valid) {
      setState({ kind: "invalid", reason: validation.reason });
      return;
    }

    // Compute SHA-256
    let sha256: string;
    try {
      sha256 = await computeSHA256(file);
    } catch {
      setState({ kind: "invalid", reason: "Failed to compute file hash. The file may be unreadable." });
      return;
    }

    setState({ kind: "checking", file, sha256 });

    // Check if a report already exists for this hash (GET, no quota usage)
    const token = await getClerkAuthToken();
    const result = await checkReputationExists(sha256, token);

    if (result.found && result.verdict !== "unknown") {
      setState({ kind: "found", file, sha256, verdict: result.verdict });
    } else {
      setState({ kind: "ready", file, sha256 });
    }
  }, []);

  async function handleUpload() {
    if (state.kind !== "ready" && state.kind !== "found") return;

    const { file, sha256 } = state;
    setState({ kind: "uploading", file, sha256 });

    const token = await getClerkAuthToken();
    const result = await uploadSample(file, sha256, token);

    if (result.ok) {
      setState({ kind: "idle" });
    } else {
      setState({ kind: "error", message: result.error, file, sha256 });
    }
  }

  function handleClear() {
    setState({ kind: "idle" });
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  function handleClick() {
    inputRef.current?.click();
  }

  function handleInputChange(event: JSX.TargetedEvent<HTMLInputElement, Event>) {
    const file = event.currentTarget.files?.[0] ?? null;
    handleFile(file);
  }

  function handleDragEnter(event: JSX.TargetedDragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }

  function handleDragOver(event: JSX.TargetedDragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }

  function handleDragLeave(event: JSX.TargetedDragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }

  function handleDrop(event: JSX.TargetedDragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const file = event.dataTransfer?.files?.[0] ?? null;
    if (file) {
      handleFile(file);
    }
  }

  const stateModifier = getStateModifier(state.kind, isDragOver);

  return (
    <div
      class={`upload-zone${stateModifier}`}
      role="button"
      tabIndex={0}
      aria-label="File upload zone"
      onClick={handleClick}
      onKeyDown={(event: JSX.TargetedKeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick();
        }
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        class="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleInputChange}
      />

      {/* Icon */}
      <div class="upload-zone__icon" aria-hidden="true">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="6" y="18" width="36" height="24" rx="3" stroke="currentColor" stroke-width="1.5" />
          <path d="M24 34V20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          <path d="M18 26L24 20L30 26" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M12 18V12C12 10.3431 13.3431 9 15 9H33C34.6569 9 36 10.3431 36 12V18" stroke="currentColor" stroke-width="1.5" />
        </svg>
      </div>

      {renderStateContent(state, handleUpload, handleClear)}
    </div>
  );
}

function renderStateContent(
  state: UploadState,
  onUpload: () => void,
  onClear: () => void
) {
  switch (state.kind) {
    case "idle":
      return (
        <div class="upload-zone__prompt">
          <span class="upload-zone__prompt-primary">Drop files here or click to browse</span>
          <span class="upload-zone__prompt-secondary">PE, ELF, Mach-O, PDF, Office, scripts, archives (max 20 MB)</span>
        </div>
      );

    case "invalid":
      return <div class="upload-zone__error-msg">{state.reason}</div>;

    case "has-file":
    case "checking":
    case "ready":
    case "found":
    case "uploading": {
      const sha256 =
        "sha256" in state && state.sha256 ? state.sha256 : null;
      const file = "file" in state ? state.file : null;
      return (
        <>
          {file && (
            <div class="upload-zone__file-info">
              <span class="upload-zone__file-name">{file.name}</span>
              <span class="upload-zone__file-size">{formatSize(file.size)}</span>
            </div>
          )}

          {sha256 && <div class="upload-zone__sha256">{sha256}</div>}

          {state.kind === "checking" && (
            <div class="upload-zone__status">Checking if already analyzed...</div>
          )}

          {state.kind === "found" && (
            <div class="upload-zone__status">Already in catalog — {state.verdict}</div>
          )}

          {state.kind === "ready" && (
            <div class="upload-zone__status">File is new — ready to upload</div>
          )}

          {state.kind === "uploading" && (
            <div class="upload-zone__status">Uploading...</div>
          )}

          {(state.kind === "ready" || state.kind === "found") && (
            <div class="upload-zone__actions">
              {state.kind === "found" ? (
                <a
                  href={`/lookup?hash=${state.sha256}`}
                  class="upload-zone__view-btn"
                  onClick={(e: JSX.TargetedMouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
                >
                  View report
                </a>
              ) : (
                <button
                  class="upload-zone__upload-btn"
                  onClick={(e: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    onUpload();
                  }}
                >
                  Upload
                </button>
              )}
              <button
                class="upload-zone__clear-btn"
                onClick={(e: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onClear();
                }}
              >
                Clear
              </button>
            </div>
          )}
        </>
      );
    }

    case "error":
      return (
        <>
          {"file" in state && state.file && (
            <div class="upload-zone__file-info">
              <span class="upload-zone__file-name">{state.file.name}</span>
              <span class="upload-zone__file-size">{formatSize(state.file.size)}</span>
            </div>
          )}
          {"sha256" in state && state.sha256 && (
            <div class="upload-zone__sha256">{state.sha256}</div>
          )}
          <div class="upload-zone__error-msg">{state.message}</div>
          <div class="upload-zone__actions">
            <button
              class="upload-zone__upload-btn"
              onClick={(e: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onUpload();
              }}
            >
              Retry
            </button>
            <button
              class="upload-zone__clear-btn"
              onClick={(e: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onClear();
              }}
            >
              Clear
            </button>
          </div>
        </>
      );
  }
}

function getStateModifier(kind: string, isDragOver: boolean): string {
  if (isDragOver) return " upload-zone--dragover";

  switch (kind) {
    case "invalid":
      return " upload-zone--invalid";
    case "error":
      return " upload-zone--error";
    case "checking":
      return " upload-zone--checking";
    case "ready":
      return " upload-zone--ready";
    case "found":
      return " upload-zone--found";
    case "uploading":
      return " upload-zone--uploading";
    case "has-file":
      return " upload-zone--has-file";
    default:
      return "";
  }
}


